/**
 * Die EINE Stelle, an der die URL-Grammatik steht (Spezifikation § 3).
 *
 * BEWUSST OHNE `window`: reine Funktionen auf Zeichenketten. Das ist der Grund, warum
 * `test/adresse.test.ts` ohne jsdom auskommt und die Grammatik vollstaendig durchmessen kann, bevor
 * irgendeine Komponente sie benutzt. Die Verbindung zum Router liegt in `useAnsicht.ts`.
 *
 * NACHTRAG (Auftrag "Bestellboard-Filter datentypgerecht bauen", 2026-09-01): "und ohne React" gilt
 * seither nicht mehr WOERTLICH - der Import unten aus `lib/orderList.ts` (fuer `Spaltenfilter`/
 * `Bereichsfilter`/`orderListFilterKind`, reine Typen und eine reine Funktion) zieht KEINE
 * React-Abhaengigkeit nach, sondern bleibt in derselben Kategorie wie diese Datei selbst: reine
 * Zeichenketten-/Wertlogik ohne DOM-Bezug. `test/adresse.test.ts` kommt deshalb weiterhin ohne jsdom
 * aus - die urspruengliche Kernaussage ("reine Funktionen auf Zeichenketten, kein DOM/Router-Bezug")
 * bleibt zutreffend, nur die woertliche Formulierung "ohne React" ist seither ungenau.
 *
 * DER ARRAY-INDEX VON AUFGABEN UND DOKUMENTEN GEHOERT HIER NICHT HEREIN (Spezifikation § 1.2). Er
 * verschiebt sich, sobald jemand einen Eintrag loescht, und liesse verschickte Links still auf
 * etwas anderes zeigen. Die URL traegt `order_tasks.id` bzw. `doc_type`; den Index rechnet der
 * jeweilige Reiter daraus aus.
 */
import type { DrawerTabId } from "../components/Drawer/drawerRechte.ts";
import type { Seite } from "./berechtigungen.ts";
import {
  EINGEBUCHT_ZUSTAENDE,
  ORDER_LIST_BOOLEAN_KEYS,
  ORDER_LIST_DATE_KEYS,
  ORDER_LIST_EINGEBUCHT_KEYS,
  ORDER_LIST_MONEY_KEYS,
  orderListFilterKind,
  wirksameZustaende,
} from "../lib/orderList.ts";
import type { Bereichsfilter, EingebuchtFilter, EingebuchtZustand, Spaltenfilter } from "../lib/orderList.ts";
import { bestellungPfadTeile } from "../lib/bestellungLink.ts";
import type { BereichKey, QuellenFilter, WoFilter } from "../api/aktivitaeten.ts";

/**
 * Wie `Navigation.tsx:28` - die Lieferantenflaeche und die Seite "Zahlungsvorlagen" sind Seiten ohne
 * Reiter (Zugang nur ueber das Marken-Menue). `activities` ist die Seite "Aktivitaeten" (Reiter nach
 * der Uebersicht, URL-Kuerzel `aktivitaeten`). `kiErlauben` ist die Seite, auf die ein KI-Werkzeug
 * (ChatGPT, Claude) den Kollegen leitet (`/ki-erlauben?anfrage=<id>`), `kiZugaenge` die Seite
 * "Meine KI-Zugaenge" (Marken-Menue); beide ohne Reiter. `dashboard` ist die Seite "Dashboard" (Reiter nach dem Zeitstrahl,
 * URL-Kuerzel `dashboard`); sie hat keinen eigenen Ansichtszustand in der Adresse.
 */
export type Seitenkennung =
  | Seite
  | "suppliers"
  | "paymentTemplates"
  | "liquidity"
  | "activities"
  | "dashboard"
  | "kiErlauben"
  | "kiZugaenge";

/**
 * Der Ansichtszustand der Seite "Aktivitaeten" (`/aktivitaeten`). `dir` ist die Sortierrichtung der
 * Seite; die API-Abfrage nennt sie `sort` (die Seite bildet `dir` auf `sort` ab). Die Filterfelder
 * (`zr`, `von`, `bis`, `wer`, `bereich`, `feld`, `wo`, `quelle`, `q`) tragen den Zustand der Filterleiste und
 * stehen mit `dir` in der Adresse (`schreibAbfrage`/`lesAbfrage`; Plan Aktivitaeten Phase 1, Entscheidung 10;
 * `wo` seit Phase 2, Task 11B: die Objektgruppe, wiederholt, zwischen `feld` und `quelle`).
 */
export interface AktivitaetenAnsicht {
  zr: "heute" | "7t" | "30t" | "alle";
  /** `YYYY-MM-DD`; gesetzt -> `zr` wird ignoriert. */
  von: string | null;
  bis: string | null;
  wer: string[];
  bereich: BereichKey[];
  feld: string[];
  wo: WoFilter[];
  quelle: QuellenFilter[];
  q: string;
  dir: "desc" | "asc";
}

/** Deckungsgleich mit `AufgabenAnsicht` (`AufgabenDetail.tsx:53`), aber mit der STABILEN Kennung
 *  statt des Array-Index. Die Umrechnung geschieht in `TasksTab.tsx`. */
export type Aufgabenauswahl =
  | { kind: "task"; taskId: string }
  | { kind: "phase"; phaseId: string }
  | null;

/**
 * Erlaubte Werte je EINMAL als Liste, die sowohl den Typ ableitet als auch `auswahl(...)` in
 * `lesAbfrage` speist (Fixrunde 1, Befund 6). Vorher standen dieselben Werte zweimal da - einmal
 * als (fehlender) Typliteral, einmal als Array in `lesAbfrage` - und konnten auseinanderlaufen,
 * ohne dass Typpruefung oder Test es bemerkt haetten.
 */
const ARCHIVFILTER = ["active", "all", "archived"] as const;
export type Archivfilter = (typeof ARCHIVFILTER)[number];

/**
 * Die MOEGLICHEN Board-Zustaende (Auftrag 06, "Zustandsmenge statt Archivwert"). Anders als der
 * frueher alleinige `ARCHIVFILTER` (der die drei ANSICHTSWERTE active/all/archived beschrieb) ist
 * das die fachliche MENGE: das Board kann aktive, archivierte UND geloeschte Bestellungen gleichzeitig
 * zeigen. `all` gibt es hier bewusst NICHT - "alle" ist die Menge `["active","archived"]` (bzw. bei
 * ausdruecklicher Auswahl zusaetzlich `deleted`), kein eigener Aufzaehlungswert.
 *
 * Die Reihenfolge IST die kanonische Schreibreihenfolge (Regel 8 des Auftragsvertrags): nur so ist
 * die Adresse deterministisch (`?state=active&state=deleted`).
 */
export const BOARD_STATES = ["active", "archived", "deleted"] as const;
export type BoardState = (typeof BOARD_STATES)[number];
export type BoardStateSelection = readonly BoardState[];

/**
 * Bringt eine beliebige Wertfolge auf die kanonische Zustandsmenge (Auftrag 06, E1):
 *
 * - Unbekannte Werte werden STILL verworfen (§ 3.4 Regel 2), Duplikate entfernt.
 * - `none` - allein oder gemischt - ergibt IMMER die leere Menge (Regel 2): es ist der
 *   Adress-Marker fuer "bewusst nichts ausgewaehlt" und darf nicht neben echten Werten stehen.
 * - Das Ergebnis steht in kanonischer Reihenfolge (Regel 1/8).
 */
export function normalisiereBoardStates(values: Iterable<string>): BoardStateSelection {
  const menge = new Set<BoardState>();
  for (const wert of values) {
    if (wert === "none") return [];
    if ((BOARD_STATES as readonly string[]).includes(wert)) menge.add(wert as BoardState);
  }
  return BOARD_STATES.filter((zustand) => menge.has(zustand));
}

/**
 * Liest die Zustandsmenge aus der Adresse (Auftrag 06, E1):
 *
 * - Wiederholter `state`-Parameter (Regel 5: hat Vorrang vor `archiv`).
 * - Fehlt `state` ganz, greift der LEGACY-Rueckfall auf `archiv` (Regel 3/4): `active -> ["active"]`,
 *   `archived -> ["archived"]`, `all -> ["active","archived"]`. `deleted` entsteht daraus NIE
 *   rueckwirkend.
 * - Weder `state` noch (bekanntes) `archiv` -> Vorgabe `["active"]`.
 */
export function liesBoardStates(params: URLSearchParams): BoardStateSelection {
  const werte = params.getAll("state");
  if (werte.length > 0) return normalisiereBoardStates(werte);
  const archiv = params.get("archiv");
  if (archiv === "archived") return ["archived"];
  if (archiv === "all") return ["active", "archived"];
  return ["active"];
}

/**
 * Schreibt die Zustandsmenge in die Adresse (Auftrag 06, E1) - Gegenstueck zu `liesBoardStates`:
 *
 * - Die leere Menge wird als `state=none` geschrieben (Regel 6), damit sie vom "gar kein Parameter"
 *   (das die Vorgabe `["active"]` bedeutet) unterscheidbar bleibt.
 * - Die Vorgabe `["active"]` wird NICHT geschrieben (Regel 7, § 3.3).
 * - Sonst je Wert ein `state`-Eintrag in kanonischer Reihenfolge (Regel 8).
 */
export function schreibeBoardStates(params: URLSearchParams, states: BoardStateSelection): void {
  const normalisiert = normalisiereBoardStates(states);
  if (normalisiert.length === 0) {
    params.set("state", "none");
    return;
  }
  if (normalisiert.length === 1 && normalisiert[0] === "active") return;
  for (const zustand of normalisiert) params.append("state", zustand);
}

/**
 * Erlaubte Werte der Aktivitaeten-Filter. `ZEITRAEUME` als Liste wie `ARCHIVFILTER` oben; die beiden
 * Schluesselmengen als `Record`, damit der Compiler die Vollstaendigkeit gegen die Typen aus
 * `api/aktivitaeten.ts` erzwingt (ein neuer `BereichKey` ohne Eintrag hier ist ein Typfehler, nicht ein
 * still verworfener Link). KEIN Wertimport aus der API-Schicht: diese Datei bleibt frei von Netz und
 * `window` (Kopfkommentar).
 */
const ZEITRAEUME = ["heute", "7t", "30t", "alle"] as const;

const BEREICH_SCHLUESSEL: Record<BereichKey, true> = {
  status: true,
  termine: true,
  transport: true,
  preise: true,
  zahlungen: true,
  dokumente: true,
  positionen: true,
  aufgaben: true,
  stammdaten: true,
  fx: true,
  objektstammdaten: true,
  verwaltung: true,
  sonstiges: true,
};

const WO_SCHLUESSEL: Record<WoFilter, true> = {
  bestellung: true,
  sendung: true,
  fx: true,
  lieferant: true,
  vorlage: true,
  verwaltung: true,
};

const QUELLEN_SCHLUESSEL: Record<QuellenFilter, true> = {
  tool: true,
  jtl: true,
  dsv: true,
  system: true,
  migration: true,
};

const istBereichKey = (wert: string): wert is BereichKey => Object.hasOwn(BEREICH_SCHLUESSEL, wert);
const istQuellenFilter = (wert: string): wert is QuellenFilter => Object.hasOwn(QUELLEN_SCHLUESSEL, wert);
const istWoFilter = (wert: string): wert is WoFilter => Object.hasOwn(WO_SCHLUESSEL, wert);

/**
 * Ein wirklicher Kalendertag als `YYYY-MM-DD` mit Jahr 0001 bis 9999 (der 30. Februar ist keiner, der
 * 29.02.2026 auch nicht), sonst `null`. `setUTCFullYear` statt `Date.UTC(jahr, ...)`: Letzteres bildet die
 * Jahre 0 bis 99 auf 1900 bis 1999 ab, und ein Datumsfeld meldet beim Tippen des Jahres jede Ziffer als
 * Datum (0002, 0020, 0202, 2026) - faellt eine Zwischenstufe hier heraus, leert sich das Feld.
 */
function alsKalendertag(roh: string | null): string | null {
  const teile = roh?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!roh || !teile) return null;
  const [jahr, monat, tag] = [Number(teile[1]), Number(teile[2]), Number(teile[3])];
  if (jahr < 1) return null;
  const datum = new Date(0);
  datum.setUTCFullYear(jahr, monat - 1, tag);
  const stimmt = datum.getUTCFullYear() === jahr && datum.getUTCMonth() === monat - 1 && datum.getUTCDate() === tag;
  return stimmt ? roh : null;
}

/** Ein wiederholter Parameter ohne leere Werte und ohne Duplikate, in Adressreihenfolge. */
function einmalig(werte: readonly string[]): string[] {
  return [...new Set(werte.filter((wert) => wert !== ""))];
}

const AKTIONSARTEN = ["", "payment", "eta", "master"] as const;
export type Aktionsart = (typeof AKTIONSARTEN)[number];

const AUFGABENFILTER = ["open", "overdue", "mine"] as const;
export type Aufgabenfilter = (typeof AUFGABENFILTER)[number];

/**
 * Bislang der einzige Eintrag - eigens als Liste statt eines nackten String-Literaltyps, aus
 * demselben Grund wie bei `ARCHIVFILTER` (Fixrunde 1, Befund 6): ein zweiter Dialog soll hier
 * angehaengt werden koennen, ohne die Erlaubtheitspruefung (`alsDialog` unten) an zwei Stellen
 * pflegen zu muessen.
 */
const DIALOGE = ["vorlagen"] as const;
export type Dialogart = (typeof DIALOGE)[number];

export interface Ansicht {
  seite: Seitenkennung;
  /** `null` = kein Drawer offen. `kennung` ist Bestellnummer ODER UUID (§ 3.7). */
  bestellung: {
    kennung: string;
    tab: DrawerTabId;
    /** Nur bei `tab === "tasks"` von Bedeutung; `normalisiere` raeumt es sonst weg. */
    aufgabe: Aufgabenauswahl;
    /** `doc_type`. Nur bei `tab === "docs"` von Bedeutung; sonst `null`. */
    dokument: string | null;
    /**
     * `data-sec`-Schluessel eines Drawer-Abschnitts, zu dem der Drawer nach dem Oeffnen springt
     * (Klick auf eine nicht aenderbare Grid-Zelle, `lib/zellZiel.ts`). Optional und NUR gesetzt,
     * wenn vorhanden: so bleibt jede bestehende Ansicht (`{kennung, tab, aufgabe, dokument}`)
     * unveraendert gleich. Steht in der Abfrage (`?abschnitt=`), nicht im Pfad - die Pfadgrammatik
     * hat je Reiter eine eigene Bedeutung der Restsegmente. Ein Reiterwechsel setzt ihn zurueck
     * (`App.tsx#onTab`), das Schliessen des Drawers sowieso.
     */
    abschnitt?: string | null;
  } | null;
  board: {
    q: string;
        /**
         * Die MENGE der anzuzeigenden Board-Zustaende (Auftrag 06, E2) - ersetzt das fruehere Einzel-Feld
         * `archiv`. Adress-Grammatik und Legacy-Abbildung siehe `liesBoardStates`/`schreibeBoardStates`
         * oben.
         */
        states: BoardStateSelection;
        sort: string;
    dir: "asc" | "desc";
    /**
     * Ein Wert je Spaltenschluessel, Form je nach Datentyp der Spalte (`lib/orderList.ts#
     * Spaltenfilterwert`/`orderListFilterKind`) - Mehrfachauswahl, Von/Bis-Bereich oder Boolean. Bis
     * zum Auftrag "Bestellboard-Filter datentypgerecht bauen" (2026-09-01) war das ein reines
     * `Record<string, string>` (Exakttreffer); die URL-Kodierung der neuen Formen steht bei
     * `lesAbfrage`/`schreibAbfrage` unten.
     */
    spaltenfilter: Spaltenfilter;
    /**
     * Nur Bestellungen mit mindestens einem neuen Artikel (`?neu=1`). Je Seite getrennt - das
     * Board und der Zeitstrahl haben je ihr eigenes `neu`. Vorgabe `false` wird nicht geschrieben;
     * gelesen schaltet nur genau `"1"` ein (`liesNeu`).
     */
    neu: boolean;
  };
  uebersicht: { aktion: Aktionsart; aufgaben: Aufgabenfilter };
  aktivitaeten: AktivitaetenAnsicht;
  /** `neu`: wie bei `board.neu` (`?neu=1`), aber nur fuer den Zeitstrahl. */
  zeitstrahl: { q: string; dir: "asc" | "desc"; neu: boolean };
  lieferanten: { q: string; id: string | null };
  nutzer: { id: string | null };
  /**
   * Die Seite "Zahlungsvorlagen" (`/zahlungsvorlagen`, Tieflink `/zahlungsvorlagen/<id>`): `id` ist
   * die Vorlagenkennung (Slug) im PFAD, nicht in der Abfrage - ein Link aus der Lieferantenmaske
   * zeigt direkt auf genau diese Vorlage. `null` = keine Auswahl (nur die Liste).
   */
  zahlungsvorlagen: { id: string | null };
  /**
   * Die Erlauben-Seite des KI-Zugangs (`/ki-erlauben?anfrage=<id>`): `anfrage` ist die Anfragekennung, mit
   * der `GET /oauth/authorize` hierher umleitet. Sie steht in der ABFRAGE (wie `lieferanten.id`) und gehoert
   * nur zu dieser Seite; `null` = keine Kennung in der Adresse (die Seite sagt es selbst).
   */
  kiErlauben: { anfrage: string | null };
  /**
   * Der globale Vorlagen-Dialog (`components/TaskTemplate/VorlagenDialog.tsx`, ausgeloest ueber
   * `App.tsx#taskTemplateOeffnen`) - anders als `board`/`uebersicht`/... KEIN Block, der nur fuer
   * eine einzelne `seite` gilt, sondern ein eigenstaendiger Query-Parameter (`?dialog=vorlagen`),
   * lesbar/schreibbar auf JEDER Seite gleichzeitig. `null` = kein Dialog offen, das ist zugleich
   * die Vorgabe und wird beim Schreiben weggelassen (§ 3.3).
   */
  dialog: Dialogart | null;
}

export const SEITE_ZU_KUERZEL: Record<Seitenkennung, string> = {
  board: "board",
  timeline: "zeitstrahl",
  dashboard: "dashboard",
  overview: "uebersicht",
  activities: "aktivitaeten",
  payments: "zahlungen",
  liquidity: "liquiditaet",
  costs: "kosten",
  suppliers: "lieferanten",
  users: "nutzer",
  paymentTemplates: "zahlungsvorlagen",
  kiErlauben: "ki-erlauben",
  kiZugaenge: "ki-zugaenge",
};

export const KUERZEL_ZU_SEITE: Record<string, Seitenkennung> = Object.fromEntries(
  Object.entries(SEITE_ZU_KUERZEL).map(([kennung, kuerzel]) => [kuerzel, kennung as Seitenkennung]),
);

/**
 * `main` fehlt ABSICHTLICH: es ist der Vorgabereiter und wird nie geschrieben - sonst stuende
 * `/bestellung/BST-2026-0815/bestellung` da (Spezifikation § 3.2).
 */
export const TAB_ZU_KUERZEL: Record<Exclude<DrawerTabId, "main">, string> = {
  ship: "spedition",
  // Nachgezogen beim Merge von main in den Zweig "Reiter Anlieferung" (2026-08-26): der Reiter kam
  // in diesem Zweig hinzu, die Tieflinks-Aufgabe auf main kannte ihn noch nicht. Der `Record`-Typ
  // ueber `DrawerTabId` hat die Luecke beim Typecheck gemeldet - ohne Eintrag waere
  // `TAB_ZU_KUERZEL[b.tab]` fuer diesen Reiter `undefined` und die Adresse endete auf "/undefined".
  arrival: "anlieferung",
  tasks: "tasks",
  docs: "dokumente",
  pay: "zahlung",
  calc: "ek",
  // Nachgezogen fuer Task 11 (Reiter "Historie", Auftrag "Bestellungs-Aenderungshistorie",
  // 2026-09-04) - derselbe Fund wie bei `arrival` oben: `Record<Exclude<DrawerTabId, "main">, string>`
  // meldete die Luecke beim Typecheck, ohne Eintrag waere `TAB_ZU_KUERZEL["history"]` `undefined`.
  history: "historie",
};

export const KUERZEL_ZU_TAB: Record<string, DrawerTabId> = Object.fromEntries(
  Object.entries(TAB_ZU_KUERZEL).map(([kennung, kuerzel]) => [kuerzel, kennung as DrawerTabId]),
);

/**
 * Frische Unterobjekte bei jeder Ableitung (Fixrunde 1, Befund 2). Ein `{ ...VORGABE_ANSICHT }` ist
 * FLACH - `board`, `spaltenfilter` und die uebrigen Bloecke waeren dann dasselbe Objekt wie in der
 * Konstanten, und ein einziger schreibender Zugriff eines Aufrufers (z. B. `useState` in `App.tsx`)
 * vergiftete die Vorgabe fuer alle folgenden Leser. Die `toEqual`-Tests koennen das nicht sehen:
 * sie vergleichen das vergiftete Objekt mit sich selbst.
 *
 * EXPORTIERT seit der Fixrunde zu Aufgabe 5 (Befund "Gering", flacher Spread in
 * `reiterGedaechtnis.ts`): dort galt bislang `{ ...VORGABE_ANSICHT, seite }` - derselbe Fehler wie
 * oben beschrieben, nur mit der eingefrorenen Konstante statt eines flachen Aufrufs. `zielFuerReiter`
 * benutzt seither diese Funktion statt der Konstante.
 */
export function frischeVorgabe(): Ansicht {
  return {
    seite: "board",
    bestellung: null,
    board: { q: "", states: ["active"], sort: "name", dir: "desc", spaltenfilter: {}, neu: false },
    uebersicht: { aktion: "", aufgaben: "open" },
    aktivitaeten: { zr: "7t", von: null, bis: null, wer: [], bereich: [], feld: [], wo: [], quelle: [], q: "", dir: "desc" },
    zeitstrahl: { q: "", dir: "asc", neu: false },
    lieferanten: { q: "", id: null },
    nutzer: { id: null },
    zahlungsvorlagen: { id: null },
    kiErlauben: { anfrage: null },
    dialog: null,
  };
}

export const VORGABE_ANSICHT: Ansicht = Object.freeze(frischeVorgabe()) as Ansicht;

export function normalisiere(ansicht: Ansicht): Ansicht {
  if (!ansicht.bestellung) return ansicht;
  if (ansicht.bestellung.kennung === "") {
    // Eine leere Kennung schriebe `/board/bestellung/` - das liest `lesen` mangels drittem
    // Pfadabschnitt als "kein Drawer offen" zurueck. Ohne diese Regel waere der Rundlauf fuer
    // diesen Fall nicht stabil (Fixrunde 1, Befund 7).
    return { ...ansicht, bestellung: null };
  }
  const { tab } = ansicht.bestellung;
  return {
    ...ansicht,
    bestellung: {
      ...ansicht.bestellung,
      aufgabe: tab === "tasks" ? ansicht.bestellung.aufgabe : null,
      dokument: tab === "docs" ? ansicht.bestellung.dokument : null,
    },
  };
}

/**
 * `decodeURIComponent` WIRFT bei kaputter Prozentfolge (`"100%"`, `"%E0%A4%A"`). In einer Adresse,
 * die aus einer umgebrochenen Mail stammt, ist das der Normalfall - und ein Wurf hier waere ab
 * Aufgabe 2 ein Renderabbruch statt einer Vorgabeansicht (§ 3.4 Regel 2). Fixrunde 1, Befund 1.
 */
function sicherDekodieren(roh: string): string {
  try {
    return decodeURIComponent(roh);
  } catch { // still: eine kaputte Prozentfolge in der Adresse (umgebrochene Mail) ist der Normalfall - es gilt der Rohtext, die Ansicht faellt auf die Vorgabe
    return roh;
  }
}

/**
 * Trennt Seite, Bestellung und Reiter aus dem Pfad.
 *
 * Die Kurzform `/bestellung/<kennung>` (§ 3.5) faellt hier auf die Vorgabeseite - sie behauptet
 * bewusst KEINE Seite, damit ein verschickter Bestell-Link nicht am Seitenrecht des Empfaengers
 * scheitert.
 *
 * Ein UNBEKANNTES Seitenkuerzel (Tippfehler, veralteter Link) bekommt dieselbe Behandlung: der
 * erste Abschnitt wird verworfen, der REST bleibt zur Auswertung erhalten statt komplett zu
 * verfallen. Die Seite ist nur Kulisse, das Ziel des Links ist die Bestellung dahinter (§ 5.2) -
 * ein Tippfehler in der Kulisse darf sie nicht mitreissen (Fixrunde 1, Befund 9).
 */
function zerlegePfad(pfad: string): { seite: Seitenkennung; rest: string[] } {
  const teile = pfad.split("/").filter(Boolean);
  if (teile[0] === "bestellung") return { seite: VORGABE_ANSICHT.seite, rest: teile };
  const seite = KUERZEL_ZU_SEITE[teile[0] ?? ""];
  return { seite: seite ?? VORGABE_ANSICHT.seite, rest: teile.slice(1) };
}

export function lesen(pfad: string, abfrage: string): Ansicht {
  const { seite, rest } = zerlegePfad(pfad);
  const ansicht: Ansicht = { ...frischeVorgabe(), seite };

  // Die Vorlagenkennung steht direkt hinter dem Seitenkuerzel; ein folgendes `bestellung/...` (Drawer)
  // bleibt davon unberuehrt.
  if (seite === "paymentTemplates" && rest[0] !== undefined && rest[0] !== "bestellung") {
    ansicht.zahlungsvorlagen = { id: sicherDekodieren(rest[0]) };
    rest.shift();
  }

  if (rest[0] === "bestellung" && rest[1]) {
    const tab = KUERZEL_ZU_TAB[rest[2] ?? ""] ?? "main";
    ansicht.bestellung = {
      kennung: sicherDekodieren(rest[1]),
      tab,
      // `phase` kann keine Aufgabenkennung sein - die sind UUIDs. Dieselbe Formunterscheidung wie
      // bei der Bestellkennung (§ 3.8), keine zweite Regelart.
      aufgabe:
        tab !== "tasks" || !rest[3]
          ? null
          : rest[3] === "phase"
            ? rest[4]
              ? { kind: "phase", phaseId: sicherDekodieren(rest[4]) }
              : null
            : { kind: "task", taskId: sicherDekodieren(rest[3]) },
      dokument: tab === "docs" && rest[3] ? sicherDekodieren(rest[3]) : null,
    };
    const abschnitt = new URLSearchParams(abfrage).get("abschnitt");
    if (abschnitt) ansicht.bestellung.abschnitt = abschnitt;
  }

  return normalisiere(lesAbfrage(ansicht, abfrage));
}

export function schreiben(ansicht: Ansicht): string {
  const teile = [SEITE_ZU_KUERZEL[ansicht.seite]];
  if (ansicht.seite === "paymentTemplates" && ansicht.zahlungsvorlagen.id) {
    teile.push(encodeURIComponent(ansicht.zahlungsvorlagen.id));
  }
  const b = normalisiere(ansicht).bestellung;
  if (b) {
    // Die reine Pfadbildung steht in `lib/bestellungLink.ts`: der Server baut den Link der KI-Werkzeuge mit demselben Quelltext.
    teile.push(...bestellungPfadTeile(b.kennung));
    if (b.tab !== "main") teile.push(TAB_ZU_KUERZEL[b.tab]);
    if (b.aufgabe?.kind === "task") teile.push(encodeURIComponent(b.aufgabe.taskId));
    if (b.aufgabe?.kind === "phase") teile.push("phase", encodeURIComponent(b.aufgabe.phaseId));
    if (b.dokument) teile.push(encodeURIComponent(b.dokument));
  }
  let abfrageText = schreibAbfrage(ansicht);
  if (b?.abschnitt) {
    const abfrage = new URLSearchParams(abfrageText);
    abfrage.set("abschnitt", b.abschnitt);
    abfrageText = `?${abfrage.toString()}`;
  }
  return `/${teile.join("/")}${abfrageText}`;
}

/**
 * Die Kodierung EINES Spaltenfilters, je nach Datentyp (`lib/orderList.ts#orderListFilterKind`) -
 * das Gegenstueck zu `liesSpaltenfilterEintrag` unten. An der bestehenden Konvention ausgerichtet
 * (§ 3.3: `f.<Spaltenschluessel>`, am ERSTEN Punkt getrennt, ein Spaltenschluessel darf selbst Punkte
 * enthalten) statt neu erfunden:
 *
 * - Mehrfachauswahl ("enum"/"text"): der Schluessel wird MEHRFACH gesetzt, ein `p.append` je
 *   ausgewaehltem Wert (`URLSearchParams` erlaubt das nativ) - `lesAbfrage` liest wiederholte
 *   Schluessel als Array. Ein alter Tieflink mit genau EINEM Wert liest sich dadurch automatisch als
 *   1-elementige Auswahl (Rueckwaertskompatibilitaet, siehe Auftragsbrief).
 * - Bereich ("date"): ZWEI eigene Schluessel `f.<key>.von`/`f.<key>.bis`, dazu - exklusiv WIRKSAM,
 *   aber NICHT exklusiv GESCHRIEBEN, siehe `lib/orderList.ts#Bereichsfilter` - `f.<key>.leer=1` fuer
 *   "nur Zeilen ohne Wert" (Nachbesserung W1). Ein gleichzeitig gesetztes Von/Bis wird MIT
 *   geschrieben, nicht verworfen: `DatumsBereichFilter.tsx` sperrt die Felder nur optisch, ihr
 *   Inhalt soll den Tieflink-Rundlauf ueberleben, damit er beim Wiederabwaehlen von "Nur ohne Wert"
 *   zurueckkehrt, statt spurlos zu verschwinden (Nachbesserung 2, "bei Gelegenheit"-Befund: vor
 *   dieser Korrektur schrieb ein fruehzeitiges `return` nach `.leer=1` nie Von/Bis, der Rundlauf
 *   verwarf sie also DOCH, obwohl der Kopfkommentar an der Komponente das Gegenteil behauptete). Nur
 *   fuer die vier festen Datumsspalten (`due`/`etdPi`/`etd`/`eta`) - die tragen NIE einen Punkt im
 *   eigenen Namen (anders als ein Custom-Feld), die Endung ist deshalb eindeutig lesbar.
 * - Bereich je Waehrung ("money"): DREI Punkte, `f.value.<Waehrung>.von`/`.bis`, dieselbe
 *   Nicht-exklusiv-Schreibregel wie beim Datum fuer `f.value.<Waehrung>.leer=1` - `value` ist die
 *   einzige Betragsspalte und traegt ebenfalls nie selbst einen Punkt. Der waehrungsunabhaengige
 *   "ohne Wert"-Zustand (Nachbesserung W2) nutzt dabei das reservierte Pseudo-Waehrungskuerzel
 *   `__EMPTY__` (`f.value.__EMPTY__.leer=1`) - kein echter ISO-Waehrungscode kollidiert damit.
 * - Boolean ("boolean"): `f.payment=1`/`f.payment=0` (Ja/Nein). Der dritte Zustand "Alle" schreibt gar
 *   keinen Schluessel (§ 3.3, wie jeder Vorgabewert).
 * - Ware eingebucht ("eingebucht", Spalte `booked`, Auftrag 2026-10-20): Datumsbereich als
 *   `f.booked.von`/`f.booked.bis` (wie beim Datum, aber ohne `.leer`) PLUS die Zustaende als
 *   wiederholter Schluessel `f.booked=vollstaendig&f.booked=teilweise&f.booked=nichts`, in kanonischer
 *   Reihenfolge. Sind ALLE drei Zustaende gewaehlt (oder keiner), schraenkt die Auswahl nicht ein und
 *   schreibt nichts (§ 3.3, wie jeder Vorgabewert). Die frueheren Boolean-Links `f.booked=1`/`=0`
 *   bleiben lesbar, siehe `liesSpaltenfilterEintrag`.
 */
function schreibSpaltenfilterEintrag(p: URLSearchParams, spalte: string, wert: unknown): void {
  const kind = orderListFilterKind(spalte);
  if (kind === "boolean") {
    if (typeof wert === "boolean") p.set(`f.${spalte}`, wert ? "1" : "0");
    return;
  }
  if (kind === "eingebucht") {
    const filter = (wert as EingebuchtFilter) ?? {};
    if (filter.von) p.set(`f.${spalte}.von`, filter.von);
    if (filter.bis) p.set(`f.${spalte}.bis`, filter.bis);
    for (const zustand of wirksameZustaende(filter) ?? []) p.append(`f.${spalte}`, zustand);
    return;
  }
  if (kind === "date") {
    const bereich = wert as Bereichsfilter;
    if (bereich?.ohneWert) p.set(`f.${spalte}.leer`, "1");
    if (bereich?.von) p.set(`f.${spalte}.von`, bereich.von);
    if (bereich?.bis) p.set(`f.${spalte}.bis`, bereich.bis);
    return;
  }
  if (kind === "money") {
    for (const [waehrung, bereich] of Object.entries((wert as Record<string, Bereichsfilter>) ?? {})) {
      if (bereich?.ohneWert) p.set(`f.${spalte}.${waehrung}.leer`, "1");
      if (bereich?.von) p.set(`f.${spalte}.${waehrung}.von`, bereich.von);
      if (bereich?.bis) p.set(`f.${spalte}.${waehrung}.bis`, bereich.bis);
    }
    return;
  }
  // "enum"/"text": Mehrfachauswahl.
  for (const einzelwert of (wert as string[]) ?? []) {
    if (einzelwert !== "") p.append(`f.${spalte}`, einzelwert);
  }
}

/**
 * Das Gegenstueck zu `schreibSpaltenfilterEintrag` oben - liest EINEN Query-Parameter (`rest` ist der
 * Teil nach `f.`, `wert` bereits als nichtleer geprueft) in `spaltenfilter` ein, inklusive
 * Rueckwaertskompatibilitaet zu Tieflinks von vor dem Auftrag "Bestellboard-Filter datentypgerecht
 * bauen" (2026-09-01): ein Tieflink mit dem ALTEN Exakttreffer-Wert bleibt lesbar, nur die
 * fachliche Deutung aendert sich (§ 3.4 Regel 2 gilt sinngemaess auch fuer diese Uebergangsform -
 * kein alter Link soll nach diesem Auftrag ins Leere laufen). Das schliesst NACHBESSERUNG W1/W2 ein:
 * der alte Facettenwert `__EMPTY__` auf einer Datums-/Betragsspalte (z. B. `f.due=__EMPTY__`,
 * `f.value=__EMPTY__`) zeigte vor diesem Auftrag "Zeilen ohne Wert" - das bleibt erhalten, statt
 * (wie zwischenzeitlich, GEMELDET) als acht woertliche Zeichen im Von/Bis-Feld zu landen bzw. als
 * unlesbarer Wert komplett zu verschwinden.
 */
function liesSpaltenfilterEintrag(spaltenfilter: Spaltenfilter, rest: string, wert: string): void {
  // 1) Bereichsfilter MIT Endung (".von"/".bis"/".leer") - Datum direkt, Betrag ueber eine zweite
  //    Endung mit dem Waehrungskuerzel (oder dem reservierten Pseudo-Kuerzel `__EMPTY__`). Nur die
  //    festen System-Schluessel oben tragen diese Form; ein Custom-Feld, dessen Name zufaellig auf
  //    ".von"/".bis"/".leer" endet, faellt unten auf die generische Mehrfachauswahl zurueck (mit dem
  //    VOLLEN `rest`, nicht dem abgeschnittenen `kopf`).
  const endung = rest.match(/^(.+)\.(von|bis|leer)$/);
  if (endung) {
    const [, kopf, ende] = endung;
    if (ORDER_LIST_EINGEBUCHT_KEYS.has(kopf)) {
      // Ware eingebucht: nur Von/Bis. Ein `.leer` kennt diese Spalte nicht (`nichts` deckt "nicht
      // eingebucht" ab) und wird verworfen, statt als Phantomfilter unter dem vollen Namen zu landen.
      if (ende !== "leer") {
        const bisher = (spaltenfilter[kopf] as EingebuchtFilter | undefined) ?? {};
        spaltenfilter[kopf] = { ...bisher, [ende]: wert };
      }
      return;
    }
    if (ORDER_LIST_DATE_KEYS.has(kopf)) {
      const bisher = (spaltenfilter[kopf] as Bereichsfilter | undefined) ?? {};
      spaltenfilter[kopf] =
        ende === "leer" ? { ...bisher, ohneWert: wert === "1" } : { ...bisher, [ende]: wert };
      return;
    }
    const geld = kopf.match(/^(.+)\.([^.]+)$/);
    if (geld && ORDER_LIST_MONEY_KEYS.has(geld[1])) {
      const [, geldSchluessel, waehrung] = geld;
      const bisher = (spaltenfilter[geldSchluessel] as Record<string, Bereichsfilter> | undefined) ?? {};
      const bereichBisher = bisher[waehrung] ?? {};
      spaltenfilter[geldSchluessel] = {
        ...bisher,
        [waehrung]:
          ende === "leer"
            ? { ...bereichBisher, ohneWert: wert === "1" }
            : { ...bereichBisher, [ende]: wert },
      };
      return;
    }
  }

  // 2) Ware eingebucht ("booked", Auftrag 2026-10-20) - die Zustaende als wiederholter Schluessel.
  //    Die frueheren Boolean-Kodierungen bleiben lesbar (Rueckwaertskompatibilitaet): "1" und der
  //    alte Facettenwert "booked" hiessen "Ja" = nur vollstaendig; "0" und "__EMPTY__" hiessen "Nein"
  //    = alles, was nicht vollstaendig ist, also teilweise + nichts. Ein unbekannter Wert faellt still
  //    weg (§ 3.4 Regel 2) und legt keinen Filter an.
  if (ORDER_LIST_EINGEBUCHT_KEYS.has(rest)) {
    const neu: readonly EingebuchtZustand[] =
      wert === "1" || wert === "booked"
        ? ["vollstaendig"]
        : wert === "0" || wert === "__EMPTY__"
          ? ["teilweise", "nichts"]
          : EINGEBUCHT_ZUSTAENDE.filter((zustand) => zustand === wert);
    if (neu.length === 0) return;
    const bisher = (spaltenfilter[rest] as EingebuchtFilter | undefined) ?? {};
    spaltenfilter[rest] = {
      ...bisher,
      zustaende: EINGEBUCHT_ZUSTAENDE.filter(
        (zustand) => bisher.zustaende?.includes(zustand) || neu.includes(zustand),
      ),
    };
    return;
  }

  // 3) Boolean ("payment", "zoll") - "1"/"0" ist die Kodierung; "__EMPTY__" der alte Facettenwert fuer "Nein"
  //    (Rueckwaertskompatibilitaet).
  if (ORDER_LIST_BOOLEAN_KEYS.has(rest)) {
    if (wert === "1") spaltenfilter[rest] = true;
    else if (wert === "0" || wert === "__EMPTY__") spaltenfilter[rest] = false;
    return;
  }

  const kind = orderListFilterKind(rest);
  // 4) Datum/Betrag OHNE Endung - ein alter Exakttreffer-Tieflink. `__EMPTY__` (Nachbesserung
  //    W1/W2) ist dabei KEIN Datum/Betrag, sondern der alte Facetten-Leerwert - liest sich als
  //    `ohneWert`, nicht als Ein-Punkt-Bereich auf die acht Zeichen "__EMPTY__" selbst.
  if (kind === "date") {
    if (wert === "__EMPTY__") {
      const bisher = (spaltenfilter[rest] as Bereichsfilter | undefined) ?? {};
      spaltenfilter[rest] = { ...bisher, ohneWert: true };
      return;
    }
    const bisher = (spaltenfilter[rest] as Bereichsfilter | undefined) ?? {};
    spaltenfilter[rest] = { von: bisher.von ?? wert, bis: bisher.bis ?? wert };
    return;
  }
  if (kind === "money") {
    if (wert === "__EMPTY__") {
      const bisher = (spaltenfilter[rest] as Record<string, Bereichsfilter> | undefined) ?? {};
      spaltenfilter[rest] = { ...bisher, __EMPTY__: { ...bisher.__EMPTY__, ohneWert: true } };
      return;
    }
    const [waehrung, betrag] = wert.split(":");
    if (!waehrung || betrag === undefined) return; // unlesbarer alter Wert - still verwerfen (§ 3.4)
    const bisher = (spaltenfilter[rest] as Record<string, Bereichsfilter> | undefined) ?? {};
    const bereichBisher = bisher[waehrung] ?? {};
    spaltenfilter[rest] = {
      ...bisher,
      [waehrung]: { von: bereichBisher.von ?? betrag, bis: bereichBisher.bis ?? betrag },
    };
    return;
  }

  // 5) Mehrfachauswahl ("enum"/"text") - ein wiederholter Schluessel wird zum Array; ein einzelner
  //    alter Wert liest sich dadurch automatisch als 1-elementige Auswahl.
  const bisherigeWerte = Array.isArray(spaltenfilter[rest]) ? (spaltenfilter[rest] as string[]) : [];
  spaltenfilter[rest] = [...bisherigeWerte, wert];
}

/**
 * Der Filter "Neue Artikel" (`?neu=1`, je Seite): `true` wird als `neu=1` geschrieben, `false`
 * (die Vorgabe) gar nicht (§ 3.3).
 */
function schreibeNeu(p: URLSearchParams, neu: boolean): void {
  if (neu) p.set("neu", "1");
}

/**
 * Gegenstueck zu `schreibeNeu`: NUR genau `"1"` schaltet ein. `neu=0`, `neu=true`, `neu=` und ein
 * fehlender Parameter sind alle aus (§ 3.4 Regel 2 - kein Absturz, keine Meldung). Bei doppeltem
 * `neu` gilt der erste Wert (`URLSearchParams.get`).
 */
function liesNeu(p: URLSearchParams): boolean {
  return p.get("neu") === "1";
}

/**
 * Vorgabewerte werden WEGGELASSEN (§ 3.3). Eine unberuehrte Ansicht hat gar keine Abfrage - ohne
 * diese Regel traegt jeder Link rund 80 Zeichen Rauschen und niemand verschickt ihn gern.
 */
function schreibAbfrage(ansicht: Ansicht): string {
  const p = new URLSearchParams();
  const setze = (name: string, wert: string, vorgabe: string): void => {
    if (wert !== vorgabe) p.set(name, wert);
  };

  if (ansicht.seite === "board") {
    setze("q", ansicht.board.q, "");
    schreibeBoardStates(p, ansicht.board.states);
    setze("sort", ansicht.board.sort, VORGABE_ANSICHT.board.sort);
    setze("dir", ansicht.board.dir, VORGABE_ANSICHT.board.dir);
    for (const [spalte, wert] of Object.entries(ansicht.board.spaltenfilter)) {
      schreibSpaltenfilterEintrag(p, spalte, wert);
    }
    schreibeNeu(p, ansicht.board.neu);
  } else if (ansicht.seite === "overview") {
    setze("aktion", ansicht.uebersicht.aktion, "");
    setze("aufgaben", ansicht.uebersicht.aufgaben, VORGABE_ANSICHT.uebersicht.aufgaben);
  } else if (ansicht.seite === "activities") {
    // Reihenfolge = Reihenfolge der Filterleiste; Listen als wiederholter Parameter, leere Werte
    // entfallen. `q` wird getrimmt geschrieben - aus Leerzeichen allein wird kein `q`. `zr` wird auch
    // bei gesetztem von/bis geschrieben (die Leiste setzt es dann auf die Vorgabe, also faellt es weg).
    const a = ansicht.aktivitaeten;
    const vorgabe = VORGABE_ANSICHT.aktivitaeten;
    const haenge = (name: string, werte: readonly string[]): void => {
      for (const wert of werte) if (wert !== "") p.append(name, wert);
    };
    setze("zr", a.zr, vorgabe.zr);
    if (a.von) p.set("von", a.von);
    if (a.bis) p.set("bis", a.bis);
    haenge("wer", a.wer);
    haenge("bereich", a.bereich);
    haenge("feld", a.feld);
    haenge("wo", a.wo);
    haenge("quelle", a.quelle);
    setze("q", a.q.trim(), "");
    setze("dir", a.dir, vorgabe.dir);
  } else if (ansicht.seite === "timeline") {
    setze("q", ansicht.zeitstrahl.q, "");
    setze("dir", ansicht.zeitstrahl.dir, VORGABE_ANSICHT.zeitstrahl.dir);
    schreibeNeu(p, ansicht.zeitstrahl.neu);
  } else if (ansicht.seite === "suppliers") {
    setze("q", ansicht.lieferanten.q, "");
    if (ansicht.lieferanten.id) p.set("id", ansicht.lieferanten.id);
  } else if (ansicht.seite === "users") {
    if (ansicht.nutzer.id) p.set("id", ansicht.nutzer.id);
  } else if (ansicht.seite === "kiErlauben") {
    if (ansicht.kiErlauben.anfrage) p.set("anfrage", ansicht.kiErlauben.anfrage);
  }

  // `dialog` haengt NICHT am `else if` oben - er gilt seitenunabhaengig (siehe Kopfkommentar an
  // `Ansicht.dialog`). Ein `board`-Filter UND ein offener Vorlagen-Dialog muessen gleichzeitig in
  // derselben Abfrage stehen koennen.
  if (ansicht.dialog) p.set("dialog", ansicht.dialog);

  const text = p.toString();
  return text ? `?${text}` : "";
}

/** Ungueltige Werte fallen STILL auf die Vorgabe (§ 3.4 Regel 2) - der Empfaenger kann nichts
 *  dafuer und nichts dagegen tun, eine Meldung waere reiner Laerm. */
function auswahl<T extends string>(roh: string | null, erlaubt: readonly T[], vorgabe: T): T {
  return roh !== null && (erlaubt as readonly string[]).includes(roh) ? (roh as T) : vorgabe;
}

/**
 * Wie `auswahl()` oben, aber mit `null` als Vorgabe statt eines Aufzaehlungswerts - `auswahl`s
 * `T extends string` liesse `null` als Vorgabe nicht zu. `dialog` ist der einzige Zustand, der
 * ganz FEHLEN kann statt nur auf einen von mehreren Werten zu fallen; dieselbe Robustheitsregel
 * gilt trotzdem: ein unbekannter oder fehlender Wert wird still zu `null` (§ 3.4 Regel 2).
 */
function alsDialog(roh: string | null): Dialogart | null {
  return roh !== null && (DIALOGE as readonly string[]).includes(roh) ? (roh as Dialogart) : null;
}

/**
 * Die GEGENRICHTUNG von `auswahl()` - fuer die Seiten, die einen Aufzaehlungswert nicht LESEN,
 * sondern SCHREIBEN (`BoardSeite.tsx#setzeArchiv`, `UebersichtSeite.tsx#setAktionsart`/
 * `#setAufgabenfilter`). Dort stand bis Aufgabe 4, Fixrunde 1 ein blindes `as Archivfilter` -
 * bestand zwar deckungsgleich mit der Aufzaehlung, aber ohne Pruefung: ein fuenfter Filterknopf mit
 * einem neuen, noch nicht gelisteten Wert waere lautlos durch `auswahl()` gefallen (sie kennt nur
 * `erlaubt`, faellt bei allem anderen still auf die Vorgabe zurueck) - der Knopf haette beim
 * naechsten Laden/Zurueck wirkungslos ausgesehen, ohne dass irgendwo ein Fehler stand.
 *
 * Diese Funktion PRUEFT stattdessen VOR dem Schreiben und WIRFT bei einem unbekannten Wert - ein
 * echter Fehler an der Stelle, an der ein neuer Filterknopf angeschlossen wird, statt eines
 * schweigenden Nichts drei Schichten weiter weg.
 */
function pruefeAuswahl<T extends string>(wert: string, erlaubt: readonly T[]): T {
  if ((erlaubt as readonly string[]).includes(wert)) return wert as T;
  throw new Error(`Unbekannter Wert "${wert}" - erlaubt sind: ${erlaubt.join(", ")}`);
}

export function alsArchivfilter(wert: string): Archivfilter {
  return pruefeAuswahl(wert, ARCHIVFILTER);
}

export function alsAktionsart(wert: string): Aktionsart {
  return pruefeAuswahl(wert, AKTIONSARTEN);
}

export function alsAufgabenfilter(wert: string): Aufgabenfilter {
  return pruefeAuswahl(wert, AUFGABENFILTER);
}

/**
 * SPIEGELBILDLICH zu `schreibAbfrage`: es wird NUR der Block der aktiven Seite gefuellt.
 *
 * Das ist keine Stilfrage, sondern Bedingung fuer den Rundlauf. `q` und `id` kommen auf mehreren
 * Seiten vor. Fuellte diese Funktion alle Bloecke, machte `/lieferanten?q=ning&id=X` aus einer
 * Ansicht mit `nutzer.id = null` eine mit `nutzer.id = "X"` - und `lesen(schreiben(a))` waere
 * ungleich `a`, obwohl die Adresse voellig richtig ist. Der Rundlauftest faellt genau darueber.
 */
function lesAbfrage(ansicht: Ansicht, abfrage: string): Ansicht {
  const p = new URLSearchParams(abfrage);
  const q = p.get("q") ?? "";
  // `dialog` wird VOR der Seitenverzweigung gelesen und auf JEDE Rueckgabe unten gemergt - anders
  // als `q`/`id` gehoert er zu KEINEM Seitenblock, sondern gilt unabhaengig davon, welcher der
  // folgenden Zweige greift (siehe Kopfkommentar an `Ansicht.dialog`).
  const dialog = alsDialog(p.get("dialog"));

  if (ansicht.seite === "board") {
    const spaltenfilter: Spaltenfilter = {};
    for (const [name, wert] of p.entries()) {
      // Trennung am ERSTEN Punkt (§ 3.3): ein Spaltenschluessel darf selbst Punkte enthalten. Ein
      // LEERER Schluessel (`f.=x`) wird verworfen statt unter `""` abgelegt (Fixrunde 1, Befund 8).
      // Die eigentliche, datentypabhaengige Auswertung steht in `liesSpaltenfilterEintrag` oben.
      if (name.startsWith("f.") && name.length > 2 && wert !== "") {
        liesSpaltenfilterEintrag(spaltenfilter, name.slice(2), wert);
      }
    }
    return {
      ...ansicht,
      board: {
        q,
        states: liesBoardStates(p),
        sort: p.get("sort") ?? VORGABE_ANSICHT.board.sort,
        dir: auswahl(p.get("dir"), ["asc", "desc"] as const, VORGABE_ANSICHT.board.dir),
        spaltenfilter,
        neu: liesNeu(p),
      },
      dialog,
    };
  }

  if (ansicht.seite === "overview") {
    return {
      ...ansicht,
      uebersicht: {
        aktion: auswahl(p.get("aktion"), AKTIONSARTEN, VORGABE_ANSICHT.uebersicht.aktion),
        aufgaben: auswahl(p.get("aufgaben"), AUFGABENFILTER, VORGABE_ANSICHT.uebersicht.aufgaben),
      },
      dialog,
    };
  }

  if (ansicht.seite === "activities") {
    const vorgabe = VORGABE_ANSICHT.aktivitaeten;
    return {
      ...ansicht,
      aktivitaeten: {
        zr: auswahl(p.get("zr"), ZEITRAEUME, vorgabe.zr),
        von: alsKalendertag(p.get("von")),
        bis: alsKalendertag(p.get("bis")),
        wer: einmalig(p.getAll("wer")),
        bereich: einmalig(p.getAll("bereich")).filter(istBereichKey),
        feld: einmalig(p.getAll("feld")),
        wo: einmalig(p.getAll("wo")).filter(istWoFilter),
        quelle: einmalig(p.getAll("quelle")).filter(istQuellenFilter),
        q: q.trim(),
        dir: auswahl(p.get("dir"), ["asc", "desc"] as const, vorgabe.dir),
      },
      dialog,
    };
  }

  if (ansicht.seite === "timeline") {
    return {
      ...ansicht,
      zeitstrahl: {
        q,
        dir: auswahl(p.get("dir"), ["asc", "desc"] as const, VORGABE_ANSICHT.zeitstrahl.dir),
        neu: liesNeu(p),
      },
      dialog,
    };
  }

  if (ansicht.seite === "suppliers") {
    return { ...ansicht, lieferanten: { q, id: p.get("id") }, dialog };
  }

  if (ansicht.seite === "users") {
    return { ...ansicht, nutzer: { id: p.get("id") }, dialog };
  }

  if (ansicht.seite === "kiErlauben") {
    // Eine leere Kennung (`?anfrage=`) ist keine: `schreibAbfrage` laesst sie weg, der Rundlauf bleibt stabil.
    return { ...ansicht, kiErlauben: { anfrage: p.get("anfrage") || null }, dialog };
  }

  // Zahlungen und Kosten haben keinen Ansichtszustand (Spezifikation § 1.1) - `dialog` bleibt
  // trotzdem lesbar, siehe Kopfkommentar an `Ansicht.dialog`.
  return { ...ansicht, dialog };
}
